import { useDialog } from 'common/hooks/useDialog';
import { useNotification } from 'common/hooks/useNotification';
import { usePeriodicityOptions } from 'common/hooks/usePeriodicityOptions';
import {
  formatBoolean,
  formatCurrency,
  formatIban,
  formatTransactionAmount,
  normalizeIban,
} from 'common/utils/formatUtils';
import { format } from 'date-fns';
import {
  CreditCardProductFragment,
  FinancialProductType,
  useCreditCardProductQuery,
  useUpdateCreditCardProductMutation,
} from 'generated/graphql';
import { Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProductDetail } from '../ProductDetail/ProductDetail';
import { QRCodeDialog, QRCodeDialogProps } from '../QRCodeDialog/QRCodeDialog';
import { QRCodeMissingDataDialog, QRCodeMissingDataDialogProps } from '../QRCodeDialog/QRCodeMissingDataDialog';
import { ButtonRowType, DetailRowType, Section } from '../types';
import { ProductDetailDialogProps } from '../useProductDetail';
import { mapCreditCardProductToInput } from './utils';

export const CreditCardProductDetail: React.FC<ProductDetailDialogProps> = ({ id, ...props }) => {
  const { i18n, t } = useTranslation();
  const notify = useNotification();

  const { data } = useCreditCardProductQuery({
    variables: {
      id,
    },
  });

  const qrCodeDialog = useDialog<QRCodeDialogProps, void>(QRCodeDialog, {
    instanceId: 'qr-code-dialog',
    tmpNavigable: true,
  });

  const qrCodeMissingDataDialog = useDialog<QRCodeMissingDataDialogProps, void>(QRCodeMissingDataDialog, {
    instanceId: 'qr-code-missing-data-dialog',
  });

  const handleQRPayment = () => {
    const iban = data?.detail.paymentInfo?.ibanForPayment;
    const amount = data?.detail.paymentPeriod?.expectedAmount?.amount;
    if (iban && amount) {
      qrCodeDialog.create({
        amount,
        constantSymbol: data?.detail.paymentInfo?.constantSymbol ?? undefined,
        currency: data?.detail.paymentPeriod?.expectedAmount?.currency ?? 'EUR',
        iban,
        specificSymbol: data?.detail.paymentInfo?.specificSymbol ?? undefined,
        variableSymbol: data?.detail.paymentInfo?.variableSymbol ?? undefined,
      });
    } else {
      qrCodeMissingDataDialog.create({});
    }
  };

  const periodicityOptions = usePeriodicityOptions();

  const content: Section<CreditCardProductFragment, Path<CreditCardProductFragment>>[] = [
    {
      rows: [
        {
          inputType: 'text',
          label: t('products:creditCard.detail.institutionName'),
          path: 'institutionName',
          skip: !!data?.detail.institution?.id,
        } as DetailRowType<CreditCardProductFragment, 'institutionName'>,
        {
          inputType: 'text',
          label: t('products:creditCard.detail.name'),
          path: 'name',
        } as DetailRowType<CreditCardProductFragment, 'name'>,
        {
          inputType: 'text',
          label: t('products:creditCard.detail.contractNumber'),
          path: 'contractNumber',
        } as DetailRowType<CreditCardProductFragment, 'contractNumber'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:creditCard.detail.contractSignDate'),
          path: 'contractSignDate',
        } as DetailRowType<CreditCardProductFragment, 'contractSignDate'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:creditCard.detail.contractEndDate'),
          path: 'contractEndDate',
        } as DetailRowType<CreditCardProductFragment, 'contractEndDate'>,
      ],
      title: '',
    },
    {
      rows: [
        {
          formatter: (value: number) =>
            formatTransactionAmount(value, i18n.language, 2, data?.detail.paymentPeriod?.expectedAmount?.currency),
          inputEndAdornment: data?.detail.paymentPeriod?.expectedAmount?.currency,
          inputType: 'amount',
          label: t('products:creditCard.detail.instalment'),
          // we are storing paymentPeriod.expectedAmount.amount as negative number
          // but we want user to type a positive equivalent
          parse: (value) => -value,
          path: 'paymentPeriod.expectedAmount.amount',
          transform: (value) => -value,
        } as DetailRowType<CreditCardProductFragment, 'paymentPeriod.expectedAmount.amount'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:creditCard.detail.nextPayment'),
          path: 'paymentPeriod.expectedDate',
        } as DetailRowType<CreditCardProductFragment, 'paymentPeriod.expectedDate'>,
        {
          formatter: (value) =>
            periodicityOptions.find(
              (option) =>
                option.value.periodCount === value.periodCount && option.value.periodTypeCode === value.periodTypeCode,
            )?.label ?? '',
          inputType: 'radioSelect',
          label: t('products:creditCard.detail.periodType'),
          options: periodicityOptions,
          parse: (value) => ({
            expectedAmount: data?.detail.paymentPeriod?.expectedAmount,
            expectedDate: data?.detail.paymentPeriod?.expectedDate,
            periodCount: value.periodCount,
            periodTypeCode: value.periodTypeCode,
          }),
          path: 'paymentPeriod',
          transform: (value) => ({
            periodCount: value.periodCount,
            periodTypeCode: value.periodTypeCode,
          }),
        } as DetailRowType<CreditCardProductFragment, 'paymentPeriod'>,
      ],
      title: '',
    },
    {
      rows: [
        {
          inputType: 'variableSymbol',
          label: t('products:creditCard.detail.variableSymbol'),
          path: 'paymentInfo.variableSymbol',
        } as DetailRowType<CreditCardProductFragment, 'paymentInfo.variableSymbol'>,
        {
          inputType: 'specificSymbol',
          label: t('products:creditCard.detail.specificSymbol'),
          path: 'paymentInfo.specificSymbol',
        } as DetailRowType<CreditCardProductFragment, 'paymentInfo.specificSymbol'>,
        {
          inputType: 'constantSymbol',
          label: t('products:creditCard.detail.constantSymbol'),
          path: 'paymentInfo.constantSymbol',
        } as DetailRowType<CreditCardProductFragment, 'paymentInfo.constantSymbol'>,
        {
          formatter: formatIban,
          inputType: 'iban',
          label: t('products:creditCard.detail.ibanForPayment'),
          parse: normalizeIban,
          path: 'paymentInfo.ibanForPayment',
        } as DetailRowType<CreditCardProductFragment, 'paymentInfo.ibanForPayment'>,
        {
          color: 'blue.dark',
          label: t('products:creditCard.detail.qrCode'),
          onClick: handleQRPayment,
        } as ButtonRowType,
      ],
      title: t('products:creditCard.detail.paymentInfo.title'),
    },
    {
      rows: [
        {
          formatter: (value: number) => formatCurrency(value ?? 0, i18n.language),
          inputEndAdornment: t('common:inputEndAdornment.eur'),
          inputType: 'amount',
          label: t('products:creditCard.detail.limitAmount'),
          path: 'limitAmount.amount',
        } as DetailRowType<CreditCardProductFragment, 'limitAmount.amount'>,
        {
          formatter: (value: boolean) => formatBoolean(value, t),
          inputType: 'radioSelect',
          label: t('products:creditCard.detail.regularUsage'),
          options: [
            {
              label: t('common:boolean.true'),
              value: true,
            },
            {
              label: t('common:boolean.false'),
              value: false,
            },
          ],
          path: 'regularUsage',
        } as DetailRowType<CreditCardProductFragment, 'regularUsage'>,
        {
          formatter: (value: boolean) => formatBoolean(value, t),
          inputType: 'radioSelect',
          label: t('products:creditCard.detail.owedAmountPaidByMaturity'),
          options: [
            {
              label: t('common:boolean.true'),
              value: true,
            },
            {
              label: t('common:boolean.false'),
              value: false,
            },
          ],
          path: 'owedAmountPaidByMaturity',
        } as DetailRowType<CreditCardProductFragment, 'owedAmountPaidByMaturity'>,
      ],
      title: t('products:creditCard.detail.financialDetails.title', {
        date: format(data?.detail.financialDetailsUpdatedAt ?? new Date(), 'd. M. yyyy'),
      }),
    },
  ];

  const [updateProduct] = useUpdateCreditCardProductMutation({
    onError: () => {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
    },
    refetchQueries: ['CalendarEventInstances', 'Notifications', 'NotificationsCount'],
  });

  const handleUpdate = async (value: CreditCardProductFragment) => {
    await updateProduct({ variables: { id, updatedProduct: mapCreditCardProductToInput(value) } });
  };

  return (
    <ProductDetail<CreditCardProductFragment>
      {...props}
      content={content}
      data={data?.detail}
      institution={data?.detail.institution ?? undefined}
      onUpdate={handleUpdate}
      title={data?.detail.name ?? t('products:creditCard.detail.title')}
      type={FinancialProductType.CreditCard}
    />
  );
};
